import React, { useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, Info, Upload } from 'lucide-react';
import excelIcon from '@/assets/excel-icon.png';
import { ProjectHeader } from '@/components/ProjectHeader';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { demoRevisedRfiRows, type DemoRevisedRfiRow } from '@/data/demoRevisedRfiRows';

// Copied from the original Update RFI stage in main a44e741.
// The original table is retained within the current builder flow.
export function DemoRevisedRfiContent({ onContinue }: { onContinue: () => void }) {
  const [rfiStatusFilter, setRfiStatusFilter] = useState<string | null>(null);
  const [rfiRows, setRfiRows] = useState<DemoRevisedRfiRow[]>(demoRevisedRfiRows);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const updateRfiCell = (ref: string, field: keyof DemoRevisedRfiRow, value: string) => {
    setRfiRows(prev => prev.map(row => row.ref === ref ? { ...row, [field]: value } : row));
  };

  return (<div className="space-y-6">
              <div className="flex items-center gap-6 px-3 py-2 rounded-lg border border-border bg-card">
                <button onClick={() => setRfiStatusFilter(null)} className={cn("flex items-center gap-2 px-2 py-1 rounded transition-colors", rfiStatusFilter === null ? "bg-muted" : "hover:bg-muted/50")}>
                  <span className="text-sm font-semibold text-foreground">{rfiRows.length}</span>
                  <span className="text-xs text-muted-foreground">Total</span>
                </button>
                <div className="h-4 w-px bg-border" />
                <button onClick={() => setRfiStatusFilter(rfiStatusFilter === 'Open' ? null : 'Open')} className={cn("flex items-center gap-2 px-2 py-1 rounded transition-colors", rfiStatusFilter === 'Open' ? "bg-muted" : "hover:bg-muted/50")}>
                  <span className="text-sm font-semibold text-foreground">{rfiRows.filter(r => r.status === 'Open').length}</span>
                  <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/50" />
                  <span className="text-xs text-muted-foreground">Open</span>
                </button>
                <div className="h-4 w-px bg-border" />
                <button onClick={() => setRfiStatusFilter(rfiStatusFilter === 'In progress' ? null : 'In progress')} className={cn("flex items-center gap-2 px-2 py-1 rounded transition-colors", rfiStatusFilter === 'In progress' ? "bg-muted" : "hover:bg-muted/50")}>
                  <span className="text-sm font-semibold text-foreground">{rfiRows.filter(r => r.status === 'In progress').length}</span>
                  <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground/70" />
                  <span className="text-xs text-muted-foreground">In progress</span>
                </button>
                <div className="h-4 w-px bg-border" />
                <button onClick={() => setRfiStatusFilter(rfiStatusFilter === 'Closed' ? null : 'Closed')} className={cn("flex items-center gap-2 px-2 py-1 rounded transition-colors", rfiStatusFilter === 'Closed' ? "bg-muted" : "hover:bg-muted/50")}>
                  <span className="text-sm font-semibold text-foreground">{rfiRows.filter(r => r.status === 'Closed').length}</span>
                  <div className="w-1.5 h-1.5 rounded-full bg-foreground" />
                  <span className="text-xs text-muted-foreground">Closed</span>
                </button>
                <div className="h-4 w-px bg-border" />
                <button onClick={() => setRfiStatusFilter(rfiStatusFilter === 'New' ? null : 'New')} className={cn("flex items-center gap-2 px-2 py-1 rounded transition-colors", rfiStatusFilter === 'New' ? "bg-primary/10" : "hover:bg-muted/50")}>
                  <span className="text-sm font-semibold text-primary">{rfiRows.filter(r => r.isNew).length}</span>
                  <div className="w-1.5 h-1.5 rounded-full bg-primary" />
                  <span className="text-xs text-primary font-medium">New</span>
                </button>
                <div className="ml-auto flex items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => uploadInputRef.current?.click()}
                    className="text-[11px] font-medium px-2.5 py-1.5 rounded-md border border-border/50 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all flex items-center gap-1.5"
                  >
                    <Upload className="w-3 h-3" />
                    Upload
                  </button>
                  <input
                    ref={uploadInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    aria-label="Upload RFI file"
                    className="sr-only"
                    tabIndex={-1}
                    onChange={(event) => {
                      const file = event.currentTarget.files?.[0];
                      if (file) toast({ title: 'File selected', description: file.name });
                      event.currentTarget.value = '';
                    }}
                  />
                  <button
                    className="text-[11px] font-medium px-2.5 py-1.5 rounded-md border border-border/50 text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-all flex items-center gap-1.5"
                    onClick={() => {
                      const a = document.createElement('a');
                      a.href = '/Project_Beta_RFI_Updated.xlsx';
                      a.download = 'Project_Beta_RFI_Updated.xlsx';
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      toast({ title: 'Download started', description: 'Revised RFI is being downloaded.' });
                    }}
                  >
                    <img src={excelIcon} alt="Excel" className="w-4 h-4" />
                    Download
                  </button>
                  <button onClick={() => onContinue()} className="text-[11px] font-medium px-2.5 py-1.5 rounded-md bg-foreground text-background hover:bg-foreground/90 transition-all flex items-center gap-1.5">
                    Continue
                    <ArrowRight className="w-3 h-3" />
                  </button>
                </div>
              </div>

              {/* Static RFI Mockup Table */}
              <div className="border border-border rounded-lg overflow-hidden">
                <div className="max-h-[calc(100vh-220px)] overflow-y-auto">
                   <table className="w-full text-xs">
                    <colgroup>
                      <col style={{ width: 36 }} />
                      <col style={{ width: 46 }} />
                      <col style={{ width: 50 }} />
                      <col style={{ width: 50 }} />
                      <col style={{ width: 65 }} />
                      <col style={{ width: '22%' }} />
                      <col style={{ width: 75 }} />
                      <col style={{ minWidth: 350 }} />
                    </colgroup>
                    <thead>
                      <tr className="border-b border-border/40 bg-white">
                        <th className="px-2 py-2.5 text-left font-semibold text-muted-foreground uppercase tracking-wider">Ref.</th>
                        <th className="px-2 py-2.5 text-center font-semibold text-muted-foreground uppercase tracking-wider">Priority</th>
                        <th className="px-2 py-2.5 text-center font-semibold text-muted-foreground uppercase tracking-wider">Status</th>
                        <th className="px-2 py-2.5 text-left font-semibold text-muted-foreground uppercase tracking-wider">Date</th>
                        <th className="px-2 py-2.5 text-left font-semibold text-muted-foreground uppercase tracking-wider">Area</th>
                        <th className="px-2 py-2.5 text-left font-semibold text-muted-foreground uppercase tracking-wider">Request</th>
                        <th className="px-2 py-2.5 text-left font-semibold text-muted-foreground uppercase tracking-wider">File</th>
                        <th className="px-2 py-2.5 text-left font-semibold text-muted-foreground uppercase tracking-wider">FDD Comment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        const filtered = rfiRows.filter(row => !rfiStatusFilter || (rfiStatusFilter === 'New' ? row.isNew : row.status === rfiStatusFilter));
                        const sections = [...new Set(rfiRows.map(r => r.section))];
                        return sections.map(section => {
                          const sectionRows = filtered.filter(r => r.section === section);
                          if (sectionRows.length === 0 && rfiStatusFilter) return null;
                          return (
                            <React.Fragment key={section}>
                              <tr className="bg-muted/30 border-b border-border">
                                <td colSpan={8} className="px-2 py-2 font-semibold text-foreground text-xs">{section}</td>
                              </tr>
                              {sectionRows.map((row) => (
                                <tr key={row.ref} className={cn("border-b border-border hover:bg-muted/20 transition-colors", row.isNew && "bg-primary/[0.03]")}>
                                  <td className="px-2 py-2 font-mono text-foreground/80">{row.ref}</td>
                                  <td className="px-2 py-2 text-center">
                                    <div className="flex flex-col items-center gap-0.5">
                                      {row.isNew && <span className="text-[8px] font-bold text-primary bg-primary/10 px-1 py-px rounded leading-tight">NEW</span>}
                                      <select
                                        value={row.priority}
                                        onChange={e => updateRfiCell(row.ref, 'priority', e.target.value)}
                                        className={`text-[9px] font-medium px-1 py-0.5 rounded border cursor-pointer appearance-none text-center ${row.priority === 'Critical' ? 'bg-red-800/90 text-white border-red-800' : row.priority === 'High' ? 'bg-destructive/10 text-destructive border-destructive/20' : row.priority === 'Medium' ? 'bg-amber-500/10 text-amber-600 border-amber-500/20' : 'bg-muted text-muted-foreground border-border'}`}
                                        style={{ minWidth: 48 }}
                                      >
                                        <option value="Critical">Critical</option>
                                        <option value="High">High</option>
                                        <option value="Medium">Medium</option>
                                        <option value="Low">Low</option>
                                      </select>
                                    </div>
                                  </td>
                                  <td className="px-2 py-2 text-center">
                                    <select
                                      value={row.status}
                                      onChange={e => updateRfiCell(row.ref, 'status', e.target.value)}
                                      className={`text-[9px] font-medium px-1 py-0.5 rounded border cursor-pointer appearance-none text-center ${row.status === 'Open' ? 'bg-blue-500/10 text-blue-600 border-blue-500/20' : row.status === 'In progress' ? 'bg-amber-500/10 text-amber-600 border-amber-500/20' : 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20'}`}
                                      style={{ minWidth: 48 }}
                                    >
                                      <option value="Open">Open</option>
                                      <option value="In progress">In progress</option>
                                      <option value="Closed">Closed</option>
                                    </select>
                                  </td>
                                  <td className="px-2 py-2 text-foreground/70">{row.date}</td>
                                  <td className="px-2 py-2 text-foreground/85"
                                    contentEditable suppressContentEditableWarning
                                    onBlur={e => updateRfiCell(row.ref, 'area', e.currentTarget.textContent || '')}
                                    style={{ cursor: 'text', outline: 'none' }}
                                    onFocus={e => { e.currentTarget.style.boxShadow = 'inset 0 0 0 1.5px rgba(59,130,246,0.4)'; e.currentTarget.style.borderRadius = '3px'; }}
                                    onBlurCapture={e => { e.currentTarget.style.boxShadow = 'none'; }}
                                  >{row.area}</td>
                                  <td className="px-2 py-2 text-foreground/85 whitespace-pre-line"
                                    contentEditable suppressContentEditableWarning
                                    onBlur={e => updateRfiCell(row.ref, 'request', e.currentTarget.innerText || '')}
                                    style={{ cursor: 'text', outline: 'none' }}
                                    onFocus={e => { e.currentTarget.style.boxShadow = 'inset 0 0 0 1.5px rgba(59,130,246,0.4)'; e.currentTarget.style.borderRadius = '3px'; }}
                                    onBlurCapture={e => { e.currentTarget.style.boxShadow = 'none'; }}
                                  >{row.request}</td>
                                  <td className="px-2 py-2 text-foreground/70 whitespace-pre-line"
                                    contentEditable suppressContentEditableWarning
                                    onBlur={e => updateRfiCell(row.ref, 'file', e.currentTarget.textContent || '')}
                                    style={{ cursor: 'text', outline: 'none' }}
                                    onFocus={e => { e.currentTarget.style.boxShadow = 'inset 0 0 0 1.5px rgba(59,130,246,0.4)'; e.currentTarget.style.borderRadius = '3px'; }}
                                    onBlurCapture={e => { e.currentTarget.style.boxShadow = 'none'; }}
                                  >{row.file}</td>
                                  <td className="px-2 py-2 text-foreground/70 whitespace-pre-line">
                                    <div className="flex items-start gap-1">
                                      <div
                                        contentEditable suppressContentEditableWarning
                                        onBlur={e => updateRfiCell(row.ref, 'advisor', e.currentTarget.innerText || '')}
                                        style={{ cursor: 'text', outline: 'none', flex: 1, minHeight: 20 }}
                                        onFocus={e => { e.currentTarget.style.boxShadow = 'inset 0 0 0 1.5px rgba(59,130,246,0.4)'; e.currentTarget.style.borderRadius = '3px'; if (!e.currentTarget.innerText.trim()) e.currentTarget.style.color = '#1A1A1A'; }}
                                        onBlurCapture={e => { e.currentTarget.style.boxShadow = 'none'; if (!e.currentTarget.innerText.trim()) e.currentTarget.style.color = ''; }}
                                        data-placeholder="Add comment..."
                                        className={!row.advisor ? 'empty:before:content-[attr(data-placeholder)] empty:before:text-muted-foreground/40 empty:before:pointer-events-none' : ''}
                                      >{row.advisor}</div>
                                      {row.reason && (
                                        <TooltipProvider delayDuration={100}>
                                          <Tooltip>
                                            <TooltipTrigger asChild>
                                              <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-amber-100 text-amber-600 cursor-help shrink-0 mt-0.5">
                                                <Info className="w-2.5 h-2.5" />
                                              </span>
                                            </TooltipTrigger>
                                            <TooltipContent side="left" className="max-w-xs text-xs leading-relaxed p-3 bg-white text-foreground border border-border shadow-lg">
                                              <p className="font-semibold mb-1">Why this request?</p>
                                              <p className="text-foreground/70">{row.reason}</p>
                                            </TooltipContent>
                                          </Tooltip>
                                        </TooltipProvider>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </React.Fragment>
                          );
                        });
                      })()}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>);
}

export default function DemoRevisedRfi() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  const builderParams = new URLSearchParams();
  if (projectId) builderParams.set('projectId', projectId);
  const backTo = '/databook-builder' + (builderParams.size ? '?' + builderParams.toString() : '');
  const handleContinue = () => {
    builderParams.set('stage', 'qna');
    navigate('/databook-builder?' + builderParams.toString());
  };
  return (
    <div className="min-h-screen bg-background">
      <ProjectHeader backTo={backTo} />
      <main className="flex-1 overflow-y-auto px-10 py-8 bg-white">
        <DemoRevisedRfiContent onContinue={handleContinue} />
      </main>
    </div>
  );
}
